import React from "react";

import { Button, useTheme } from "@mui/material";

import { apiBaseUrls } from "@platform/constants/shared-constants";
import { useLegacyAuth } from "src/domains/auth/module/components/AuthProvider";

import { OAuthError } from "../types/OAuthError";

interface GoogleButtonProps {
  onSuccess?: (response: Record<string, unknown>) => void;
  onError?: (error: OAuthError) => void;
  disabled?: boolean;
  fullWidth?: boolean;
  /** Button label; login uses "Continue with Google Workspace", register uses "Continue with Google". */
  label?: string;
}

const GoogleButton: React.FC<GoogleButtonProps> = ({
  onSuccess,
  onError,
  disabled = false,
  fullWidth = true,
  label = "Continue with Google Workspace",
}) => {
  const { authInProgress, setAuthInProgress } = useLegacyAuth();
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  const handleGoogleLogin = async () => {
    try {
      setAuthInProgress(true);

      // Initialize Google OAuth
      const googleAuthUrl = `${apiBaseUrls.BACKEND}/auth/google`;

      // Open Google OAuth popup
      const popup = window.open(
        googleAuthUrl,
        "google-oauth",
        "width=500,height=600,scrollbars=yes,resizable=yes",
      );

      if (!popup) {
        throw new Error("Popup blocked. Please allow popups for this site.");
      }

      // Listen for message from popup
      const handleMessage = (event: MessageEvent) => {
        if (event.origin !== window.location.origin) return;

        if (event.data.type === "GOOGLE_OAUTH_SUCCESS") {
          popup.close();
          window.removeEventListener("message", handleMessage);
          setAuthInProgress(false);
          onSuccess?.(event.data.user);
        } else if (event.data.type === "GOOGLE_OAUTH_ERROR") {
          popup.close();
          window.removeEventListener("message", handleMessage);
          setAuthInProgress(false);
          onError?.(event.data.error);
        }
      };

      window.addEventListener("message", handleMessage);

      // Handle popup closed manually
      const checkClosed = setInterval(() => {
        if (popup.closed) {
          clearInterval(checkClosed);
          window.removeEventListener("message", handleMessage);
          setAuthInProgress(false);
        }
      }, 1000);
    } catch (error) {
      setAuthInProgress(false);
      onError?.(error as OAuthError);
    }
  };

  return (
    <Button
      variant="outlined"
      fullWidth={fullWidth}
      disabled={disabled || authInProgress}
      onClick={handleGoogleLogin}
      startIcon={
        <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
          <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
          <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
          <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
          <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
        </svg>
      }
      sx={{
        py: "10px",
        px: "14px",
        height: "40px",
        borderRadius: "10px",
        textTransform: "none",
        fontWeight: 500,
        fontSize: "0.8125rem",
        borderWidth: 1,
        bgcolor: isDark ? theme.palette.grey[800] : theme.palette.grey[50],
        borderColor: isDark ? theme.palette.grey[600] : theme.palette.grey[200],
        color: "text.primary",
        mb: "10px",
        gap: "10px",
        transition: "all 150ms ease",
        "& .MuiButton-startIcon": { marginRight: 0, marginLeft: 0 },
        "&:hover": {
          bgcolor: isDark ? theme.palette.grey[700] : theme.palette.grey[100],
          borderColor: isDark
            ? theme.palette.grey[500]
            : theme.palette.grey[300],
        },
        "&:active": { transform: "scale(0.98)" },
        "&:disabled": {
          bgcolor: isDark ? theme.palette.grey[800] : theme.palette.grey[50],
          borderColor: isDark
            ? theme.palette.grey[600]
            : theme.palette.grey[200],
          color: isDark ? "rgba(255,255,255,0.3)" : "rgba(0,0,0,0.3)",
        },
      }}
    >
      {authInProgress ? "Connecting..." : label}
    </Button>
  );
};

export default GoogleButton;
